/**
 * Lobby tables ("tavoli") — the public, FIFO replacement for the invisible
 * rating-based matchmaking queue.
 *
 * A table is an open offer to play, published to everyone: whoever presses the
 * same quick button sits at the OLDEST matching table, and anyone can instead
 * click a specific table in the list. It is the shareable-link challenge made
 * public — same settings, same exclusive claim, same shared game start — minus
 * the URL and plus a queue everybody can see.
 *
 * Wire types keep `variant` as a plain string, like {@link LinkChallengeInfo}:
 * the server narrows it, and a client on a cached bundle never chokes on an
 * enum value it does not know.
 */

import type { TimeControlKey, TimeControlPreset } from './time-control.js';
import type { GameMode, OpeningMode } from './game-mode.js';
import type { MatchLegs } from './match.js';
import type { ChallengeColor, MatchType } from './challenge.js';

/**
 * `quick` = created by one of the two big cadence buttons, always the same
 * shape (free opening, normal rules, random colour) so the FIFO pool is
 * interchangeable. `custom` = created from the full options form.
 */
export type LobbyTableKind = 'quick' | 'custom';

/** Lifecycle of a table. `starting` is the instant between claim and game start. */
export type LobbyTableStatus = 'open' | 'starting' | 'started' | 'cancelled' | 'expired';

/**
 * Public projection of a table. Deliberately carries no user ids — it is
 * broadcast to every connected client, guests included.
 */
export interface LobbyTableInfo {
  id: string;
  kind: LobbyTableKind;
  /** Display name of whoever opened the table. */
  hostUsername: string;
  /**
   * `null` when the host has no rating at all — a guest or a bot. Only a
   * registered human owns one, so this is genuinely absent rather than zero:
   * the row must render nothing, not a `0`.
   */
  hostRating: number | null;
  hostAvatar: string | null;
  /**
   * A guest host can only ever offer a casual game, and the list says so. The
   * flag is public because it explains the `rated: false` next to it.
   */
  hostIsGuest: boolean;
  variant: string;
  timeControl: TimeControlKey;
  /** Side the HOST asked for; whoever sits gets the other one. */
  color: ChallengeColor;
  /**
   * Who the host will play, and whether the result counts. `'rated'` is the only
   * type that keeps guests and bots out — and so the only one that can promise
   * the game will count. See {@link MatchType}.
   */
  matchType: MatchType;
  /**
   * Whether the host is offering a rated game. Not the final word: a guest
   * sitting down at an `'any'` table downgrades it to casual at start time
   * (`shouldRateGame`). Derived from `matchType`; kept for cached bundles.
   */
  rated: boolean;
  gameMode: GameMode;
  opening: OpeningMode;
  /** 2 = andata e ritorno, 1 = solo andata. Always 1 with a free opening. */
  legs: MatchLegs;
  /** True when the game starts from a composed position (board editor). */
  hasPosition: boolean;
  /** Epoch ms. The FIFO order is exactly this, ascending. */
  createdAt: number;
  expiresAt: number;
}

/**
 * How many people are connected, split the way the lobby shows it. Bots are in
 * neither number: they are staff, not company.
 */
export interface LobbyCounts {
  registered: number;
  guests: number;
}

/** Why a table left the list, so the client can word it. */
export type LobbyRemovalReason = 'started' | 'cancelled' | 'expired';

export interface LobbyTableRemoved {
  tableId: string;
  reason: LobbyRemovalReason;
}

/** `lobby:list` ack. */
export interface LobbySnapshot {
  tables: LobbyTableInfo[];
  counts: LobbyCounts;
  /** The caller's own open table, so a reopened overlay shows "in coda". */
  myTableId: string | null;
}

/** `lobby:quick` request. */
export interface LobbyQuickRequest {
  variant: string;
  timeControl: TimeControlPreset;
}

/**
 * `lobby:quick` ack. Either you sat at someone's table and the game is already
 * running, or yours was published and you are waiting.
 */
export type LobbyQuickResponse =
  | { seated: true; roomId: string; tableId: string }
  | { seated: false; tableId: string };

/** `lobby:sit` ack. */
export interface LobbySitResponse {
  roomId: string;
  color: 'white' | 'black';
}

export interface LobbyError {
  code: LobbyErrorCode;
  message: string;
}

export type LobbyErrorCode =
  | 'AUTH_REQUIRED'
  | 'RATE_LIMITED'
  | 'ALREADY_IN_GAME'
  | 'IN_COMPETITION'
  | 'INVALID_TIME_CONTROL'
  | 'INVALID_VARIANT'
  | 'INVALID_SETTINGS'
  | 'TABLE_NOT_FOUND'
  | 'TABLE_TAKEN'
  | 'OWN_TABLE'
  | 'REGISTERED_ONLY'
  /**
   * Chi aveva aperto il tavolo non e' piu' raggiungibile nell'istante in cui ci
   * si siede. Distinto da `START_FAILED`: non e' andato storto niente, e'
   * l'avversario che non c'e' — e chi si e' seduto resta libero di sedersi
   * altrove invece di finire in una stanza con un posto vuoto.
   */
  | 'HOST_GONE'
  | 'START_FAILED';

/**
 * The cadences the quick buttons offer. Single source of truth, in THIS order:
 * the server validates against it and the overlay renders it left to right, so
 * neither the list nor the order can drift.
 *
 * A ladder, shortest first, and each rung has its own reason to be here:
 *
 *  - `bullet-2+1` is the SHORT one, the rung the row was missing. It is also the
 *    canonical bullet preset the dashboard already names for its Bullet rating
 *    chip (`CATEGORY_DEFAULT_PRESET` / `tcPresetFor`), so pressing it moves
 *    exactly the number that chip shows. It took the place of `blitz-2+5`, which
 *    is still a named preset and still the Arena Blitz Italiana's cadence — it
 *    just is not a quick button any more.
 *  - `blitz-3+2` and `blitz-5+3` are the old ranked queue's, so those rating
 *    pools keep the shape they have had all along.
 *  - `rapid-7+5` is one of the two daily arenas (`schedule_slots`, migration
 *    082), i.e. a cadence the platform already plays every day.
 *
 * Note what the two ends of the ladder bring with them: `bullet-2+1` makes quick
 * pairing feed the `bullet` pool for the first time, and `rapid-7+5` the `rapid`
 * pool. Both used to be blitz only; blitz still gets 3+2 and 5+3.
 *
 * `TimeControlPreset` and not `TimeControlKey`: a cadence on the main Play sheet
 * is a cadence the platform offers, so it must be a named one. The arena keeps
 * the open key type — its calendar is edited with an UPDATE over SSH — but this
 * row is code, and the narrow type is what makes `TIME_CONTROL_PRESETS` (which
 * the re-queue gate and the remembered-cadence key read) agree with it for free.
 */
export const QUICK_TIME_CONTROLS = [
  'bullet-2+1',
  'blitz-3+2',
  'blitz-5+3',
  'rapid-7+5',
] as const satisfies readonly TimeControlPreset[];

export type QuickTimeControl = (typeof QUICK_TIME_CONTROLS)[number];

export function isQuickTimeControl(tc: string): tc is QuickTimeControl {
  return (QUICK_TIME_CONTROLS as readonly string[]).includes(tc);
}

/**
 * How long a table stays in the list without being taken (10 minutes).
 *
 * Longer than the 30s of the old queue — a visible table is worth leaving out,
 * that is the whole point — but far short of the link challenge's hour: a link
 * waits for one named friend, a table waits for whoever walks past, and a stale
 * one just makes the lobby look busier than it is.
 */
export const LOBBY_TABLE_TTL_MS = 10 * 60 * 1000;

/** Max tables one user may open per minute. */
export const LOBBY_MAX_TABLES_PER_MINUTE = 8;
